/**
 * Photos are resized on the device before upload (ARCHITECTURE.md §3.2): a
 * listing-grade master, longest side 2048 px, and a 480 px thumbnail.
 * Re-encoding through a canvas drops EXIF, including GPS. WebP where the
 * browser can encode it; Safari's canvas can't, so there it falls back to JPEG.
 */
export interface PreparedPhoto {
  master: Blob;
  thumb: Blob;
  contentType: "image/webp" | "image/jpeg";
  width: number;
  height: number;
  previewUrl: string;
}

const MASTER_EDGE = 2048;
const THUMB_EDGE = 480;

async function encode(bitmap: ImageBitmap, edge: number, prefer: "image/webp" | "image/jpeg", quality: number) {
  const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser can't process photos");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, prefer, quality));
  if (!blob) throw new Error("Couldn't encode the photo");
  return { blob, width, height };
}

export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    let master = await encode(bitmap, MASTER_EDGE, "image/webp", 0.86);
    let contentType: PreparedPhoto["contentType"] = "image/webp";
    if (master.blob.type !== "image/webp") {
      master = await encode(bitmap, MASTER_EDGE, "image/jpeg", 0.88);
      contentType = "image/jpeg";
    }
    let thumb = await encode(bitmap, THUMB_EDGE, "image/webp", 0.8);
    // The thumbnail is always stored as image/webp. Where the browser can only
    // make JPEG, browsers sniff image bytes, so the JPEG body still renders.
    if (thumb.blob.type !== "image/webp") thumb = await encode(bitmap, THUMB_EDGE, "image/jpeg", 0.8);
    return {
      master: master.blob,
      thumb: thumb.blob,
      contentType,
      width: master.width,
      height: master.height,
      previewUrl: URL.createObjectURL(thumb.blob),
    };
  } finally {
    bitmap.close();
  }
}
